import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { type ButtonHTMLAttributes, type ComponentPropsWithoutRef, forwardRef, type ReactNode } from 'react'
import { cn } from '../../lib/utils'

/**
 * Unstyled, focusable trigger for hint tooltips (info icons, access hints, relative dates), so keyboard and
 * screen reader users reach the hint too. Marked data-hint so clickable table rows still navigate when it is clicked.
 */
export const HintTrigger = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, type, ...props }, ref) => (
    <button
      ref={ref}
      type={type ?? 'button'}
      data-hint=""
      className={cn(
        'inline-flex cursor-default items-center rounded-sm border-0 bg-transparent p-0 text-left text-inherit outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
        className,
      )}
      {...props}
    />
  ),
)
HintTrigger.displayName = 'HintTrigger'

export const TooltipProvider = TooltipPrimitive.Provider
export const TooltipRoot = TooltipPrimitive.Root
export const TooltipTrigger = TooltipPrimitive.Trigger

export const TooltipContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 max-w-xs rounded-md bg-ink px-2.5 py-1.5 text-xs leading-5 text-white shadow-md',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = 'TooltipContent'

/** Tooltip around a focusable child (the child must accept a ref, e.g. a button or span with tabIndex). */
export function Tooltip({
  content,
  children,
  side,
}: {
  content: ReactNode
  children: ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
}) {
  if (content === null || content === undefined || content === '') return <>{children}</>
  return (
    <TooltipRoot>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side}>{content}</TooltipContent>
    </TooltipRoot>
  )
}
